import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Loader,
  Select,
  Switch,
  Textfield
} from '@clientbase/clientbase-library'
import {
  Box,
  Chip as MuiChip,
  Divider,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { getCreditCard } from 'api/requests'
import { useAuthContext } from 'context/AuthContext'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'
import { useRouter } from 'next/router'
import { getCreditCardPaymentOptionsText } from 'templates/Charge/sharedCharge.utils'
import { SelectCreditCards } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/BodyModalNewChargeOrRecurrence'

import {
  REQUIRES_REREGISTRATION_TOOLTIP,
  selectCreditCardsMapper
} from 'components/DrawerCreditCard/PreviewCards/PreviewCards.mapper'
import BoletoIcon from 'components/Icons/Boleto'
import CreditCardIcon from 'components/Icons/CreditCard'
import PixIcon from 'components/Icons/Pix'
import CreditCardPlanNotice from 'components/PaymentsType/components/CreditCardPlanNotice'

import { formatMoney } from 'utils'
import { priceConverter } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import InstallmentSelect from './components/InstallmentSelect'
import SimulationModal from './components/SimulationModal'
import * as S from './PaymentsType.styles'

export enum PaymentsTypeValues {
  Pix = 'pix',
  Boleto = 'boleto',
  CartaoDeCredito = 'credit_card',
  CartaoParcelado = 'credit_card_installments',
  CartaoRecorrente = 'credit_card_recurring',
  // Recebimento manual (previsão): nada é emitido, o merchant dá baixa manualmente.
  // Fica recolhido em "Outras opções de recebimento" e é excludente com os demais.
  RecebimentoManual = 'direct_payment'
}

export interface PaymentsTypeRef {
  scrollToCreditCard: () => void
}

type PaymentsTypeProps<T extends FieldValues> = {
  payments: string[] | undefined // Manter para compatibilidade
  register: UseFormRegister<T>
  control: Control<T, object>
  setValue: UseFormSetValue<T>
  customerUuid?: string | null
  errors?: FieldErrors<T>
  selectCreditCard?: boolean
  watch: UseFormWatch<T>
  label?: string
  isRecurrence?: boolean
  totalValue: number
  isUnique?: boolean
  recurrenceUuid?: string | null
  isProductOrLink?: boolean
  recurrenceHasCreditCardRecurring?: boolean
  onValidationError?: (hasError: boolean) => void
  onScrollToCreditCardReady?: (scrollFn: () => void) => void
}

const PaymentsType = forwardRef(
  <T extends FieldValues>(
    {
      payments,
      register,
      customerUuid,
      errors,
      control,
      setValue,
      watch,
      selectCreditCard = true,
      totalValue,
      label = 'Meios de pagamento',
      isUnique = false,
      isProductOrLink = false,
      isRecurrence,
      recurrenceUuid,
      recurrenceHasCreditCardRecurring = false,
      onValidationError,
      onScrollToCreditCardReady
    }: PaymentsTypeProps<T>,
    ref: React.Ref<PaymentsTypeRef>
  ) => {
    const { user } = useAuthContext()
    const router = useRouter()
    const isMerchantEnabled = user?.status === 'enabled'
    const [creditCard, setCreditCards] = useState([] as SelectCreditCards[])

    const [loading, setLoading] = useState(false)
    const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false)
    const [expandedOtherOptions, setExpandedOtherOptions] = useState(false)
    const creditCardRef = useRef<HTMLDivElement>(null)
    const previousPassOnFeesActive = useRef<boolean | null>(null)
    const previousMaxInstallmentsWithoutFee = useRef<number | null>(null)

    const scrollToCreditCard = () => {
      if (creditCardRef.current) {
        setTimeout(() => {
          creditCardRef.current?.scrollIntoView({
            behavior: 'smooth'
          })
        }, 100)
      }
    }

    useImperativeHandle(ref, () => ({
      scrollToCreditCard
    }))

    // Notificar o componente pai quando a função de scroll estiver pronta
    useEffect(() => {
      if (onScrollToCreditCardReady) {
        onScrollToCreditCardReady(scrollToCreditCard)
      }
    }, [onScrollToCreditCardReady])

    const { payment_type_details, frequency, amount, chargeType } = watch()

    // Função para obter o valor correto baseado no contexto
    const getCorrectValue = () => {
      if (isProductOrLink && amount) {
        // No contexto de produtos/links, usar o campo amount com priceConverter
        return priceConverter(amount)
      }
      // Nos outros contextos, usar totalValue calculado dos itens
      return totalValue
    }

    const correctValue = getCorrectValue()

    // Função auxiliar para verificar se um pagamento está incluído (compatibilidade)
    const includesPayment = (paymentType: string) => {
      return Array.isArray(payments) && payments.includes(paymentType)
    }

    const isForecastOnly = includesPayment(PaymentsTypeValues.RecebimentoManual)

    // Recebimento manual (previsão) é excludente: ligar zera os outros meios, desligar
    // limpa o próprio. Precisa mexer em `payments` — e não só no payment_type_details —
    // porque o useEffect de sincronização trata `payments` como fonte de verdade e
    // reverteria a mudança na passada seguinte.
    const handleToggleForecastOnly = (
      checked: boolean,
      onChange: (value: boolean) => void
    ) => {
      onChange(checked)

      if (checked) {
        setValue(
          'payments' as Path<T>,
          [PaymentsTypeValues.RecebimentoManual] as any
        )
        setValue('payment_type_details.pix.enabled' as Path<T>, false as any)
        setValue('payment_type_details.boleto.enabled' as Path<T>, false as any)
        setValue(
          'payment_type_details.boleto_pix.enabled' as Path<T>,
          false as any
        )
        setValue(
          'payment_type_details.credit_card.enabled' as Path<T>,
          false as any
        )
        setValue(
          'payment_type_details.credit_card.credit_card.enabled' as Path<T>,
          false as any
        )
        setValue(
          'payment_type_details.credit_card.credit_card_recurring.enabled' as Path<T>,
          false as any
        )
        setValue(
          'payment_type_details.credit_card.credit_card_installments.enabled' as Path<T>,
          false as any
        )
        return
      }

      setValue('payments' as Path<T>, [] as any)
    }

    // Estado visual/funcional de Cartão de Crédito: ativo se qualquer opção de cartão estiver ativa
    const isCreditCardActiveForUI = () => {
      return payment_type_details?.credit_card?.enabled || false
    }

    // Função para verificar se deve mostrar seções extras para PIX e Boleto
    // Mostra para cobrança única OU recorrência com frequência >= trimestral
    const shouldShowExtraSection = () => {
      return !isRecurrence || (isRecurrence && frequency >= 2) // Única OU (Recorrência E Trimestral+)
    }

    // Função para verificar se deve mostrar InstallmentSelect do cartão
    const forceRecurringLikeLayoutForUniqueFromRecurrence =
      !!isUnique && !!recurrenceUuid && !!recurrenceHasCreditCardRecurring

    const shouldShowInstallmentSelect = () => {
      if (forceRecurringLikeLayoutForUniqueFromRecurrence) return true
      return !isRecurrence || frequency >= 2 // Se não for recorrência OU frequência >= trimestral
    }

    // Função para verificar se deve mostrar switch de parcelamento no cartão
    // Mostra quando: não é recorrência OU (é recorrência válida E parcelas > 1)
    const shouldShowInstallmentSwitch = () => {
      if (forceRecurringLikeLayoutForUniqueFromRecurrence)
        return (payment_type_details?.credit_card?.max_installments || 1) > 1
      if (!isRecurrence)
        return (payment_type_details?.credit_card?.max_installments || 1) > 1
      if (frequency >= 2)
        return (payment_type_details?.credit_card?.max_installments || 1) > 1
      return false
    }

    // Função para verificar switches básicos (à vista e recorrência)
    // Mostra quando: não é recorrência E parcelas > 1 OU é recorrência
    const shouldShowBasicSwitches = () => {
      if (forceRecurringLikeLayoutForUniqueFromRecurrence) return true
      if (isUnique || !isRecurrence) {
        // Para cobrança única, só mostra switches se tiver mais de 1 parcela
        return (payment_type_details?.credit_card?.max_installments || 1) > 1
      }
      // Para recorrências, sempre mostra os switches
      return true
    }

    // Função para verificar se deve mostrar o switch "Renovar a cobrança com esse meio de pagamento"
    // Deve aparecer APENAS quando é recorrência E frequência é válida para parcelamento (trimestral+)
    const shouldShowRenewSwitch = () => {
      // Não mostrar se for cobrança única (chargeType === 0)
      if (!isRecurrence) return false

      // Verificar se é recorrência baseado em chargeType (1) ou isRecurrence (true)
      const isRecurrentCharge = chargeType === 1 || isRecurrence
      // Só mostra se for recorrência E frequência >= 2 (trimestral, semestral, anual)
      return isRecurrentCharge && frequency >= 2
    }

    // Função para verificar se deve mostrar o select de parcelas sem juros
    // Não mostra para recorrências mensais ou semanais (frequency < 2)
    const shouldShowInstallmentsWithoutFeeSelect = () => {
      // Para recorrências mensais ou semanais, não mostrar o select
      if (isRecurrence && frequency < 2) {
        return false
      }
      // Para outros casos, mostrar normalmente
      return true
    }

    // Função para gerar as opções de parcelas sem juros limitadas pelo installments do cartão
    const getInstallmentsWithFeeOptions = () => {
      const maxInstallments =
        payment_type_details?.credit_card?.max_installments || 1
      // Limitar as opções pelo número máximo de parcelas do cartão
      const limitedLength = Math.min(maxInstallments, 21)

      const options = []

      // Primeira opção: Repassar juros (valor 0)
      options.push({ value: 0, label: 'Repassar juros' })

      // Segunda opção: 1 parcela (valor 1)
      options.push({ value: 1, label: '1 parcela' })

      // Demais opções: 2 parcelas em diante
      for (let i = 2; i <= limitedLength; i++) {
        options.push({ value: i, label: `${i} parcelas` })
      }

      return options
    }

    // Função para calcular o máximo de parcelas baseado na frequência
    const getMaxInstallmentsByFrequency = () => {
      if (!isRecurrence) return 21 // Para cobrança única, máximo 21 parcelas

      // Para recorrências, o parcelamento não pode ultrapassar o número de meses
      // de um ciclo (senão a série de parcelas invade o ciclo seguinte).
      switch (frequency) {
        case 2: // Trimestral
          return 3
        case 3: // Semestral
          return 6
        case 4: // Anual
          return 12
        default: // Semanal (0) e Mensal (1) não parcelam
          return 1
      }
    }

    const getCards = async (customerUuid?: string) => {
      setCreditCards([])
      if (!customerUuid) return
      setLoading(true)
      try {
        const response = await getCreditCard({ uuid: customerUuid })
        const data = selectCreditCardsMapper(response.data.records ?? [])
        setCreditCards(data)
      } catch (e) {
        toast.error('Erro ao buscar cartões')
      }
      setLoading(false)
    }

    const getPixAndBoletoMessage = (
      installments: number,
      totalValue: number,
      isRecurrence: boolean,
      fixed = false
    ) => {
      const value = totalValue / installments

      if (fixed) {
        return `Serão geradas ${installments} cobranças de ${formatMoney(
          value
        )} — seu cliente não poderá alterar a quantidade de parcelas.`
      }

      if (isRecurrence) {
        return `Caso parcelado - seu cliente receberá uma fatura por mês, durante ${installments} meses, no valor de ${formatMoney(
          value
        )}. `
      }

      return `Seu cliente receberá uma fatura por mês, durante ${installments} meses, no valor de ${formatMoney(
        value
      )} cada`
    }

    useEffect(() => {
      const wantsRecurring =
        includesPayment('credit_card_recurring') ||
        payment_type_details?.credit_card?.credit_card_recurring?.enabled

      if (!customerUuid || !wantsRecurring) return
      getCards(customerUuid)
    }, [
      payments,
      customerUuid,
      payment_type_details?.credit_card?.credit_card_recurring?.enabled
    ])

    // Sincronizar checkboxes de payments com enabled no payment_type_details
    useEffect(() => {
      // Previsão é excludente e já foi aplicada por handleToggleForecastOnly: deixar
      // esta sincronização rodar reativaria os meios que acabaram de ser desligados.
      if (isForecastOnly) return

      // Sincronizar PIX
      const pixEnabled = includesPayment(PaymentsTypeValues.Pix)
      if (payment_type_details?.pix?.enabled !== pixEnabled) {
        setValue(
          'payment_type_details.pix.enabled' as Path<T>,
          pixEnabled as any
        )
      }

      // Sincronizar Boleto
      const boletoEnabled = includesPayment(PaymentsTypeValues.Boleto)
      if (payment_type_details?.boleto?.enabled !== boletoEnabled) {
        setValue(
          'payment_type_details.boleto.enabled' as Path<T>,
          boletoEnabled as any
        )
      }

      // Sincronizar Cartão de Crédito - verifica se o checkbox está marcado
      const creditCardChecked = includesPayment(
        PaymentsTypeValues.CartaoDeCredito
      )
      if (payment_type_details?.credit_card?.enabled !== creditCardChecked) {
        setValue(
          'payment_type_details.credit_card.enabled' as Path<T>,
          creditCardChecked as any
        )
      }
    }, [payments, payment_type_details, setValue, isForecastOnly])

    // Definir switches padrão baseado no tipo de cobrança
    useEffect(() => {
      // Só executa quando cartão de crédito está selecionado (qualquer variante)
      if (!isCreditCardActiveForUI()) {
        return
      }

      // No caso especial (única vinda de recorrência), não forçar resets nos switches
      if (forceRecurringLikeLayoutForUniqueFromRecurrence) {
        return
      }

      // Verifica se nenhum dos switches está ativo
      const hasInstallmentBilling =
        payment_type_details?.credit_card?.credit_card_installments?.enabled
      const hasRecurringBilling =
        payment_type_details?.credit_card?.credit_card_recurring?.enabled
      const hasMonthlyPayment =
        payment_type_details?.credit_card?.credit_card?.enabled

      // Para cobrança única à vista (1 parcela), NÃO ativar nenhum switch
      // Cobrança única à vista deve usar apenas o pagamento padrão do cartão
      if (
        (isUnique || !isRecurrence) &&
        (payment_type_details?.credit_card?.max_installments || 1) === 1
      ) {
        // Garantir que todos os switches estejam desativados para cobrança única à vista
        if (hasMonthlyPayment) {
          setValue(
            'payment_type_details.credit_card.credit_card.enabled' as Path<T>,
            false as any
          )
        }
        if (hasInstallmentBilling) {
          setValue(
            'payment_type_details.credit_card.credit_card_installments.enabled' as Path<T>,
            false as any
          )
        }
        if (hasRecurringBilling) {
          setValue(
            'payment_type_details.credit_card.credit_card_recurring.enabled' as Path<T>,
            false as any
          )
        }

        // IMPORTANTE: Para cobrança única à vista, garantir que enabled seja true
        // para que o tipo credit_card seja enviado corretamente
        setValue(
          'payment_type_details.credit_card.enabled' as Path<T>,
          true as any
        )

        return
      }

      // Para outros casos (recorrência ou cobrança única parcelada), não forçar nenhum switch
      // A validação será feita via Yup no momento do submit
    }, [
      Array.isArray(payments) && payments.includes('credit_card'),
      payment_type_details?.credit_card?.credit_card_installments?.enabled,
      payment_type_details?.credit_card?.credit_card_recurring?.enabled,
      payment_type_details?.credit_card?.credit_card?.enabled,
      payment_type_details?.credit_card?.max_installments,
      isRecurrence,
      isUnique,
      forceRecurringLikeLayoutForUniqueFromRecurrence
    ])

    // Remove crédito parcelado quando muda para cobrança recorrente
    useEffect(() => {
      if (!isUnique && includesPayment('credit_card_installments')) {
        const updatedPayments = Array.isArray(payments)
          ? payments.filter((payment) => payment !== 'credit_card_installments')
          : payments
        setValue(
          'payments' as Path<T>,
          updatedPayments as PathValue<T, Path<T>>
        )
      }
    }, [isUnique, payments, setValue])

    // Garantir que pass_card_fees e max_installments_without_fee sejam resetados quando cartão está desativado
    useEffect(() => {
      const currentPassCardFees =
        payment_type_details?.credit_card?.pass_card_fees
      const currentMaxInstallmentsWithoutFee =
        payment_type_details?.credit_card?.max_installments_without_fee

      if (!isCreditCardActiveForUI()) {
        if (currentPassCardFees !== false) {
          setValue(
            'payment_type_details.credit_card.pass_card_fees' as Path<T>,
            false as any
          )
        }
        if (currentMaxInstallmentsWithoutFee != null) {
          setValue(
            'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
            null as any
          )
        }
        previousPassOnFeesActive.current = null
        previousMaxInstallmentsWithoutFee.current = null
      }
    }, [payment_type_details?.credit_card?.enabled])

    // Ao reduzir o máximo de parcelas, o "sem juros" precisa acompanhar. Sem
    // isso, baixar de 12x para 6x deixa max_installments_without_fee em 12 e a
    // validação `lte-max-installments` bloqueia o salvamento — apontando um
    // campo que o merchant nem vê quando "Repassar taxas do Cartão" está
    // desligado. O ref também é clampado, senão o valor antigo volta ao religar
    // o switch.
    useEffect(() => {
      const maxInstallments = Number(
        payment_type_details?.credit_card?.max_installments
      )
      if (!Number.isFinite(maxInstallments) || maxInstallments <= 0) return

      const currentMaxInstallmentsWithoutFee =
        payment_type_details?.credit_card?.max_installments_without_fee

      if (
        currentMaxInstallmentsWithoutFee != null &&
        Number(currentMaxInstallmentsWithoutFee) > maxInstallments
      ) {
        setValue(
          'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
          maxInstallments as any
        )
      }

      if (
        previousMaxInstallmentsWithoutFee.current != null &&
        previousMaxInstallmentsWithoutFee.current > maxInstallments
      ) {
        previousMaxInstallmentsWithoutFee.current = maxInstallments
      }
    }, [
      payment_type_details?.credit_card?.max_installments,
      payment_type_details?.credit_card?.max_installments_without_fee,
      setValue
    ])

    // Renderiza o componente sempre, pois os campos podem ser inicializados posteriormente
    // A condição original impedia a renderização quando os campos não estavam definidos

    // Validação: quando cartão está selecionado em recorrência, deve ter pelo menos uma opção ativa
    const getCreditCardValidationError = () => {
      // Só valida se cartão estiver selecionado
      if (!isCreditCardActiveForUI()) return null

      // Para cobrança única à vista (1 parcela), não validar
      // Nesse caso, o pagamento manual é automático e não há switches para selecionar
      if (
        (isUnique || !isRecurrence) &&
        (payment_type_details?.credit_card?.max_installments || 1) === 1
      ) {
        return null
      }

      // Só valida em modo recorrência
      if (!isRecurrence) {
        return null
      }

      // Verificar se algum switch está ativo
      const hasInstallmentBilling =
        payment_type_details?.credit_card?.credit_card_installments?.enabled
      const hasRecurringBilling =
        payment_type_details?.credit_card?.credit_card_recurring?.enabled
      const hasMonthlyPayment =
        payment_type_details?.credit_card?.credit_card?.enabled

      // Se nenhum switch estiver ativo, retornar erro
      if (
        !hasInstallmentBilling &&
        !hasRecurringBilling &&
        !hasMonthlyPayment
      ) {
        const hasInstallments = !!(
          payment_type_details?.credit_card?.max_installments &&
          payment_type_details.credit_card.max_installments > 1
        )
        return getCreditCardPaymentOptionsText(!!isRecurrence, hasInstallments)
      }

      return null
    }

    const creditCardError = getCreditCardValidationError()

    // Notificar componente pai sobre erro de validação
    useEffect(() => {
      if (onValidationError) {
        onValidationError(!!creditCardError)
      }
    }, [creditCardError, onValidationError])

    return (
      <Box role="document" sx={{ mt: '32px', mb: '4px' }}>
        <Box mb={2}>
          <Typography variant="mdBold">{label}</Typography>
        </Box>

        <Stack
          sx={{
            background: '#FFFFFF',
            border: '1px solid #D7D7D7',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <S.CheckboxWrapper
            data-pixIsActivated={includesPayment(PaymentsTypeValues.Pix)}
          >
            <input
              type="checkbox"
              id={PaymentsTypeValues.Pix}
              value={PaymentsTypeValues.Pix}
              disabled={isForecastOnly}
              data-actived={includesPayment(PaymentsTypeValues.Pix)}
              style={{ position: 'absolute' }}
              checked={
                Array.isArray(payments) &&
                payments.includes(PaymentsTypeValues.Pix)
              }
              {...register('payments' as Path<T>)}
            />
            <S.Label
              htmlFor={PaymentsTypeValues.Pix}
              inputIsChecked={includesPayment(PaymentsTypeValues.Pix)}
            >
              <PixIcon /> Pix
            </S.Label>
          </S.CheckboxWrapper>

          {includesPayment(PaymentsTypeValues.Pix) &&
            shouldShowExtraSection() && (
              <>
                <Stack p={2} gap={1}>
                  <Controller
                    name={
                      'payment_type_details.pix.fixed_installments' as Path<T>
                    }
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="smBold">
                          Definir quantidade fixa de parcelas
                        </Typography>
                        <Switch
                          checked={value || false}
                          onChange={(e) => {
                            const checked = e.target.checked
                            onChange(checked)
                            if (
                              checked &&
                              Number(
                                watch(
                                  'payment_type_details.pix.max_installments' as Path<T>
                                ) || 1
                              ) < 2
                            ) {
                              setValue(
                                'payment_type_details.pix.max_installments' as Path<T>,
                                2 as any
                              )
                            }
                          }}
                        />
                      </Stack>
                    )}
                  />
                  <InstallmentSelect
                    control={control}
                    loading={loading}
                    maxInstallments={getMaxInstallmentsByFrequency()}
                    totalValue={correctValue}
                    paymentType="pix"
                    fieldName="payment_type_details.pix.max_installments"
                    fixed={
                      !!watch(
                        'payment_type_details.pix.fixed_installments' as Path<T>
                      )
                    }
                  />
                  {watch(
                    'payment_type_details.pix.max_installments' as Path<T>
                  ) > 1 && (
                    <Typography color={theme.palette.neutral[400]}>
                      {getPixAndBoletoMessage(
                        watch(
                          'payment_type_details.pix.max_installments' as Path<T>
                        ),
                        correctValue,
                        isRecurrence || false,
                        !!watch(
                          'payment_type_details.pix.fixed_installments' as Path<T>
                        )
                      )}
                    </Typography>
                  )}
                </Stack>
                <Divider sx={{ borderColor: '#D7D7D7' }} />
              </>
            )}

          <S.CheckboxWrapper
            data-pixIsActivated={includesPayment(PaymentsTypeValues.Pix)}
          >
            <input
              type="checkbox"
              id={PaymentsTypeValues.Boleto}
              value={PaymentsTypeValues.Boleto}
              disabled={isForecastOnly}
              data-actived={includesPayment(PaymentsTypeValues.Boleto)}
              style={{ position: 'absolute' }}
              checked={
                Array.isArray(payments) &&
                payments.includes(PaymentsTypeValues.Boleto)
              }
              {...register('payments' as Path<T>)}
            />
            <S.Label
              htmlFor={PaymentsTypeValues.Boleto}
              inputIsChecked={includesPayment(PaymentsTypeValues.Boleto)}
            >
              <BoletoIcon /> Boleto
            </S.Label>
          </S.CheckboxWrapper>

          {includesPayment(PaymentsTypeValues.Boleto) &&
            shouldShowExtraSection() && (
              <>
                <Stack p={2} gap={1}>
                  <Controller
                    name={
                      'payment_type_details.boleto.fixed_installments' as Path<T>
                    }
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="smBold">
                          Definir quantidade fixa de parcelas
                        </Typography>
                        <Switch
                          checked={value || false}
                          onChange={(e) => {
                            const checked = e.target.checked
                            onChange(checked)
                            if (
                              checked &&
                              Number(
                                watch(
                                  'payment_type_details.boleto.max_installments' as Path<T>
                                ) || 1
                              ) < 2
                            ) {
                              setValue(
                                'payment_type_details.boleto.max_installments' as Path<T>,
                                2 as any
                              )
                            }
                          }}
                        />
                      </Stack>
                    )}
                  />
                  <InstallmentSelect
                    control={control}
                    loading={loading}
                    maxInstallments={getMaxInstallmentsByFrequency()}
                    totalValue={correctValue}
                    paymentType="boleto"
                    fieldName="payment_type_details.boleto.max_installments"
                    fixed={
                      !!watch(
                        'payment_type_details.boleto.fixed_installments' as Path<T>
                      )
                    }
                  />
                  {watch(
                    'payment_type_details.boleto.max_installments' as Path<T>
                  ) > 1 && (
                    <Typography color={theme.palette.neutral[400]}>
                      {getPixAndBoletoMessage(
                        watch(
                          'payment_type_details.boleto.max_installments' as Path<T>
                        ),
                        correctValue,
                        isRecurrence || false,
                        !!watch(
                          'payment_type_details.boleto.fixed_installments' as Path<T>
                        )
                      )}
                    </Typography>
                  )}
                </Stack>
                <Divider sx={{ borderColor: '#D7D7D7' }} />
              </>
            )}

          <S.CheckboxWrapper
            ref={creditCardRef}
            data-pixIsActivated={includesPayment(PaymentsTypeValues.Pix)}
            id="credit-card-payment-section"
          >
            <input
              type="checkbox"
              id={PaymentsTypeValues.CartaoDeCredito}
              value={PaymentsTypeValues.CartaoDeCredito}
              disabled={isForecastOnly}
              data-actived={isCreditCardActiveForUI()}
              style={{ position: 'absolute' }}
              checked={
                Array.isArray(payments) &&
                payments.includes(PaymentsTypeValues.CartaoDeCredito)
              }
              {...register('payments' as Path<T>)}
            />
            <S.Label
              htmlFor={PaymentsTypeValues.CartaoDeCredito}
              inputIsChecked={isCreditCardActiveForUI()}
            >
              <CreditCardIcon /> Cartão de Crédito
            </S.Label>
          </S.CheckboxWrapper>

          {!isMerchantEnabled && isCreditCardActiveForUI() && (
            <Stack
              sx={{
                backgroundColor: '#FFF8E6',
                borderLeft: '4px solid #F5A623',
                p: 2,
                gap: 1.5
              }}
            >
              <Typography variant="smBold" sx={{ color: '#7A5C00' }}>
                Ative sua conta para usar cartão de crédito
              </Typography>
              <Typography sx={{ color: '#7A5C00' }}>
                Para criar cobranças, planos ou recorrências com cartão de
                crédito, é necessário concluir a ativação da sua conta.
              </Typography>
              <Button
                variantButton="pricing"
                onClick={() => router.push('/ativar-conta')}
                sx={{
                  alignSelf: 'flex-start',
                  mt: 0.5
                }}
              >
                Ativar conta
              </Button>
            </Stack>
          )}

          {isCreditCardActiveForUI() && isMerchantEnabled && (
            <>
              <CreditCardPlanNotice />

              <Stack p={2} gap={2}>
                {shouldShowInstallmentSelect() && (
                  <>
                    <Controller
                      name={
                        'payment_type_details.credit_card.fixed_installments' as Path<T>
                      }
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="smBold">
                            Definir quantidade fixa de parcelas
                          </Typography>
                          <Switch
                            checked={value || false}
                            onChange={(e) => {
                              const checked = e.target.checked
                              onChange(checked)
                              if (
                                checked &&
                                Number(
                                  watch(
                                    'payment_type_details.credit_card.max_installments' as Path<T>
                                  ) || 1
                                ) < 2
                              ) {
                                setValue(
                                  'payment_type_details.credit_card.max_installments' as Path<T>,
                                  2 as any
                                )
                              }
                            }}
                          />
                        </Stack>
                      )}
                    />
                    <InstallmentSelect
                      control={control}
                      loading={loading}
                      maxInstallments={getMaxInstallmentsByFrequency()}
                      totalValue={correctValue}
                      paymentType="credit_card"
                      fieldName="payment_type_details.credit_card.max_installments"
                      fixed={
                        !!watch(
                          'payment_type_details.credit_card.fixed_installments' as Path<T>
                        )
                      }
                    />
                  </>
                )}

                {/* Switch de Parcelamento - mostra quando parcelas > 1 */}
                {shouldShowInstallmentSwitch() && (
                  <Controller
                    name={
                      'payment_type_details.credit_card.credit_card_installments.enabled' as Path<T>
                    }
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      const handleToggle = () => {
                        onChange(!value)
                      }

                      const isRecurringWithoutInstallments =
                        isRecurrence &&
                        (frequency < 2 ||
                          (payment_type_details?.credit_card
                            ?.max_installments || 1) === 1)

                      const isRecurringWithInstallments =
                        isRecurrence &&
                        frequency >= 2 &&
                        (payment_type_details?.credit_card?.max_installments ||
                          1) > 1

                      const getSubtitle = () => {
                        if (isRecurringWithoutInstallments) {
                          return 'Cobra o valor total na hora'
                        }
                        if (isRecurringWithInstallments) {
                          return 'Cobra o valor total do ciclo e seu cliente paga parcelado no cartão'
                        }
                        return 'Cobra o valor total na hora e seu cliente paga parcelado no cartão'
                      }

                      return (
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ cursor: 'pointer' }}
                          onClick={handleToggle}
                        >
                          <Stack gap={1}>
                            <Typography variant="smBold">Parcelado</Typography>
                            <Typography
                              sx={{
                                color: theme.palette.neutral[400]
                              }}
                            >
                              {getSubtitle()}
                            </Typography>
                          </Stack>
                          <Switch checked={value || false} />
                        </Stack>
                      )
                    }}
                  />
                )}

                {/* Switches básicos - à vista e recorrência */}
                {shouldShowBasicSwitches() && (
                  <>
                    {/* Recorrência no Cartão de Crédito */}
                    <Controller
                      name={
                        'payment_type_details.credit_card.credit_card_recurring.enabled' as Path<T>
                      }
                      control={control}
                      render={({ field: { onChange, value } }) => {
                        const handleToggle = () => {
                          onChange(!value)
                        }

                        const isRecurringWithoutInstallments =
                          isRecurrence &&
                          (frequency < 2 ||
                            (payment_type_details?.credit_card
                              ?.max_installments || 1) === 1)

                        const isRecurringWithInstallments =
                          isRecurrence &&
                          frequency >= 2 &&
                          (payment_type_details?.credit_card
                            ?.max_installments || 1) > 1

                        const getSubtitle = () => {
                          if (isRecurringWithoutInstallments) {
                            return 'Cobra o valor automaticamente a cada ciclo, sem comprometer o limite do seu cliente'
                          }
                          if (isRecurringWithInstallments) {
                            return 'Cobra o valor de cada parcela automaticamente, sem comprometer o limite do seu cliente'
                          }
                          return 'Cobra o valor de cada parcela automaticamente, sem comprometer o limite do seu cliente'
                        }

                        return (
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleToggle}
                          >
                            <Stack gap={1}>
                              <Typography variant="smBold">
                                {isRecurringWithoutInstallments
                                  ? 'Cartão Recorrente'
                                  : 'Parcelado Recorrente'}
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400]
                                }}
                              >
                                {getSubtitle()}
                              </Typography>
                            </Stack>
                            <Switch checked={value || false} />
                          </Stack>
                        )
                      }}
                    />

                    {watch(
                      'payment_type_details.credit_card.credit_card_recurring.enabled' as Path<T>
                    ) &&
                      selectCreditCard &&
                      creditCard.length > 0 && (
                        <Controller
                          control={control}
                          name={'credit_card_uuid' as Path<T>}
                          render={({ field: { onChange, value } }) => (
                            <Autocomplete
                              options={creditCard}
                              value={value}
                              onChange={(
                                _,
                                option = { value: '', label: '' }
                              ) => onChange(option)}
                              // SC-10941: a API recusa vincular cartão que precisa de recadastro.
                              getOptionDisabled={(option) =>
                                !!(option as SelectCreditCards)
                                  .requiresReregistration
                              }
                              renderOption={(props, option) => {
                                const optionTyped =
                                  option as AutoCompleteBillingsParams
                                const needsReregistration = !!(
                                  option as SelectCreditCards
                                ).requiresReregistration
                                return (
                                  <Box
                                    component="li"
                                    {...props}
                                    key={optionTyped.value}
                                  >
                                    {needsReregistration ? (
                                      <Tooltip
                                        enterTouchDelay={0}
                                        placement="top-start"
                                        title={REQUIRES_REREGISTRATION_TOOLTIP}
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          gap={1}
                                        >
                                          {optionTyped.label}
                                          <MuiChip
                                            size="small"
                                            color="warning"
                                            label="Recadastro necessário"
                                          />
                                        </Stack>
                                      </Tooltip>
                                    ) : (
                                      optionTyped.label
                                    )}
                                  </Box>
                                )
                              }}
                              noOptionsText={
                                loading ? (
                                  <Loader />
                                ) : (
                                  'Não há cartões disponíveis.'
                                )
                              }
                              renderInput={(params) => (
                                <Textfield
                                  helperText={
                                    errors?.credit_card_uuid?.message as string
                                  }
                                  error={!!errors?.credit_card_uuid?.message}
                                  placeholder="Selecionar cartão"
                                  {...params}
                                />
                              )}
                            />
                          )}
                        />
                      )}

                    {/* Pagamento À Vista */}
                    <Controller
                      name={
                        'payment_type_details.credit_card.credit_card.enabled' as Path<T>
                      }
                      control={control}
                      render={({ field: { onChange, value } }) => {
                        const handleToggle = () => {
                          onChange(!value)
                        }

                        const isRecurringWithoutInstallments =
                          isRecurrence &&
                          (frequency < 2 ||
                            (payment_type_details?.credit_card
                              ?.max_installments || 1) === 1)

                        const isRecurringWithInstallments =
                          isRecurrence &&
                          frequency >= 2 &&
                          (payment_type_details?.credit_card
                            ?.max_installments || 1) > 1

                        const getSubtitle = () => {
                          if (isRecurringWithoutInstallments) {
                            return 'Envia uma nova fatura a cada ciclo para seu cliente realizar o pagamento'
                          }
                          if (isRecurringWithInstallments) {
                            return 'Envia uma nova fatura a cada parcela para seu cliente realizar o pagamento'
                          }
                          return 'Envia uma nova fatura a cada parcela para seu cliente realizar o pagamento'
                        }

                        return (
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleToggle}
                          >
                            <Stack gap={1}>
                              <Typography variant="smBold">
                                {isRecurringWithoutInstallments
                                  ? 'Cartão Manual'
                                  : 'Parcelado Manual'}
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400]
                                }}
                              >
                                {getSubtitle()}
                              </Typography>
                            </Stack>
                            <Switch checked={value || false} />
                          </Stack>
                        )
                      }}
                    />
                  </>
                )}
              </Stack>

              {shouldShowRenewSwitch() && (
                <>
                  <Divider sx={{ borderColor: '#D7D7D7' }} />
                  <Stack p={2}>
                    <Controller
                      name={
                        'payment_type_details.credit_card.automatic_renew' as Path<T>
                      }
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => onChange(!value)}
                        >
                          <Stack gap={1}>
                            <Typography variant="smBold">
                              Renovar a cobrança com esse meio de pagamento na
                              virada do ciclo
                            </Typography>
                            <Typography
                              sx={{
                                color: theme.palette.neutral[400]
                              }}
                            >
                              Válido para as opções Parcelamento no Cartão de
                              Crédito e Recorrência no Cartão de Crédito
                            </Typography>
                          </Stack>
                          <Switch
                            checked={value || false}
                            onChange={onChange}
                          />
                        </Stack>
                      )}
                    />
                  </Stack>
                </>
              )}

              <Divider sx={{ borderColor: '#D7D7D7' }} />
              <Stack p={2} gap={2}>
                <Controller
                  name={
                    'payment_type_details.credit_card.pass_card_fees' as Path<T>
                  }
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const isPassOnFeesActive = value === true

                    return (
                      <Stack gap={1}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            const newValue = !isPassOnFeesActive
                            onChange(newValue)

                            const currentMaxInstallmentsWithoutFee =
                              payment_type_details?.credit_card
                                ?.max_installments_without_fee

                            if (!newValue) {
                              // Guardar o valor atual antes de limpar
                              if (
                                currentMaxInstallmentsWithoutFee !== null &&
                                currentMaxInstallmentsWithoutFee !== undefined
                              ) {
                                previousMaxInstallmentsWithoutFee.current =
                                  currentMaxInstallmentsWithoutFee
                              }
                              setValue(
                                'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
                                null as any
                              )
                            } else {
                              // Restaurar o valor anterior ou usar 0 como padrão
                              const valueToSet =
                                previousMaxInstallmentsWithoutFee.current ?? 0
                              setValue(
                                'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
                                valueToSet as any
                              )
                            }
                          }}
                        >
                          <Stack>
                            <Typography variant="smBold">
                              Repassar taxas do Cartão
                            </Typography>
                          </Stack>
                          <Switch
                            checked={isPassOnFeesActive}
                            onChange={(e) => {
                              const newValue = e.target.checked
                              onChange(newValue)

                              const currentMaxInstallmentsWithoutFee =
                                payment_type_details?.credit_card
                                  ?.max_installments_without_fee

                              if (!newValue) {
                                // Guardar o valor atual antes de limpar
                                if (
                                  currentMaxInstallmentsWithoutFee !== null &&
                                  currentMaxInstallmentsWithoutFee !== undefined
                                ) {
                                  previousMaxInstallmentsWithoutFee.current =
                                    currentMaxInstallmentsWithoutFee
                                }
                                setValue(
                                  'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
                                  null as any
                                )
                              } else {
                                // Restaurar o valor anterior ou usar 0 como padrão
                                const valueToSet =
                                  previousMaxInstallmentsWithoutFee.current ?? 0
                                setValue(
                                  'payment_type_details.credit_card.max_installments_without_fee' as Path<T>,
                                  valueToSet as any
                                )
                              }
                            }}
                          />
                        </Stack>

                        {isPassOnFeesActive &&
                          shouldShowInstallmentsWithoutFeeSelect() && (
                            <Controller
                              name={
                                'payment_type_details.credit_card.max_installments_without_fee' as Path<T>
                              }
                              control={control}
                              render={({
                                field: {
                                  onChange: onChangeSelect,
                                  value: selectValue
                                }
                              }) => (
                                <Select
                                  helperText=""
                                  error={false}
                                  fullWidth={true}
                                  onChange={onChangeSelect}
                                  value={
                                    selectValue !== undefined &&
                                    selectValue !== null
                                      ? selectValue
                                      : ''
                                  }
                                  label={
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      gap={0.5}
                                    >
                                      <Typography>
                                        Em quantas vezes sem juros?
                                      </Typography>
                                      <Typography
                                        color={theme.palette.neutral[400]}
                                      >
                                        (Cobra as taxas ou permita parcelar sem
                                        juros)
                                      </Typography>
                                    </Stack>
                                  }
                                  size="small"
                                >
                                  <MenuItem value="" disabled>
                                    Selecione a quantidade de parcelas
                                  </MenuItem>
                                  {getInstallmentsWithFeeOptions().map(
                                    ({ label, value }) => (
                                      <MenuItem key={value} value={value}>
                                        {label}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              )}
                            />
                          )}
                      </Stack>
                    )
                  }}
                />

                <Typography
                  sx={{
                    textDecoration: 'underline',
                    ':hover': { cursor: 'pointer' },
                    width: 'fit-content'
                  }}
                  component="div"
                  onClick={() => setIsSimulationModalOpen(true)}
                >
                  Ver simulações
                </Typography>
              </Stack>
            </>
          )}
        </Stack>

        {/* Recebimento manual (previsão) — recolhido de propósito: não deve competir
            visualmente com Pix/Boleto/Cartão, que são o caminho normal. Abre sozinho
            quando já está marcado (edição), senão o merchant reabre a cobrança e não
            enxerga o meio que escolheu. */}
        <Controller
          name={'payment_type_details.direct_payment.enabled' as Path<T>}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Accordion
              expanded={expandedOtherOptions || !!value}
              onChange={(_: unknown, isExpanded: boolean) =>
                setExpandedOtherOptions(isExpanded)
              }
              sx={{ mt: 2 }}
            >
              <AccordionSummary>
                <Typography variant="smBold">
                  Outras opções de recebimento
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack gap={1}>
                  <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                  >
                    <Typography variant="smBold">
                      Recebimento manual (previsão)
                    </Typography>
                    <Switch
                      checked={!!value}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleToggleForecastOnly(event.target.checked, onChange)
                      }
                    />
                  </Stack>
                  <Typography variant="smLight" sx={{ color: '#666' }}>
                    A cobrança não será emitida nem enviada ao cliente. Ela
                    entra na sua previsão de recebimento e você dá baixa
                    manualmente quando o dinheiro chegar.
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        />

        {errors?.payments?.message && (
          <S.ErrorLabel>Selecione um método de pagamento.</S.ErrorLabel>
        )}

        {(creditCardError ||
          (errors?.payment_type_details as any)?.credit_card?.message) && (
          <S.ErrorLabel>
            {(errors?.payment_type_details as any)?.credit_card?.message ||
              creditCardError}
          </S.ErrorLabel>
        )}

        <SimulationModal
          isOpen={isSimulationModalOpen}
          onClose={() => setIsSimulationModalOpen(false)}
          totalValue={correctValue}
          maxInstallments={
            payment_type_details?.credit_card?.max_installments || 1
          }
          maxInstallmentsWithoutFee={
            !payment_type_details?.credit_card?.pass_card_fees
              ? payment_type_details?.credit_card?.max_installments || 1
              : payment_type_details?.credit_card
                  ?.max_installments_without_fee ?? 1
          }
          recurringBilling={
            payment_type_details?.credit_card?.credit_card_recurring?.enabled ||
            false
          }
          monthlyPayment={
            payment_type_details?.credit_card?.credit_card?.enabled || false
          }
          installmentBilling={
            payment_type_details?.credit_card?.credit_card_installments
              ?.enabled || false
          }
          isRecurrence={isRecurrence}
          isUnique={isUnique}
          frequency={frequency}
        />
      </Box>
    )
  }
) as (<T extends FieldValues>(
  props: PaymentsTypeProps<T> & { ref?: React.Ref<PaymentsTypeRef> }
) => React.ReactElement) & { displayName?: string }

PaymentsType.displayName = 'PaymentsType'

export default PaymentsType
